import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdAdd, MdDelete, MdSave, MdRefresh, MdDragIndicator } from 'react-icons/md';
import { FaSeedling, FaUserGraduate, FaHandsHelping, FaFireAlt, FaCrown, FaChessKing, FaTrophy, FaMedal, FaGem, FaStar, FaBolt, FaRocket, FaShieldAlt } from 'react-icons/fa';

const AVAILABLE_ICONS = [
  { id: 'seedling', icon: FaSeedling, label: 'Seedling' },
  { id: 'graduate', icon: FaUserGraduate, label: 'Graduate' },
  { id: 'hands', icon: FaHandsHelping, label: 'Hands' },
  { id: 'fire', icon: FaFireAlt, label: 'Fire' },
  { id: 'medal', icon: FaMedal, label: 'Medal' },
  { id: 'trophy', icon: FaTrophy, label: 'Trophy' },
  { id: 'crown', icon: FaCrown, label: 'Crown' },
  { id: 'king', icon: FaChessKing, label: 'King' },
  { id: 'gem', icon: FaGem, label: 'Gem' },
  { id: 'star', icon: FaStar, label: 'Star' },
  { id: 'bolt', icon: FaBolt, label: 'Bolt' },
  { id: 'rocket', icon: FaRocket, label: 'Rocket' },
  { id: 'shield', icon: FaShieldAlt, label: 'Shield' },
];

const ICON_MAP = Object.fromEntries(AVAILABLE_ICONS.map(i => [i.id, i.icon]));

function AdminPollRatings() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { loadTiers(); }, []);

  const loadTiers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/poll-rating-tiers`);
      setTiers(res.data.tiers);
      setHasChanges(false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load tiers' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate before saving
    for (const tier of tiers) {
      if (!tier.title?.trim()) {
        setMessage({ type: 'error', text: 'All tiers must have a title' });
        return;
      }
      if (tier.min === '' || tier.min === null || tier.min === undefined || tier.min < 0) {
        setMessage({ type: 'error', text: `"${tier.title}" has an invalid min threshold` });
        return;
      }
    }
    const mins = tiers.map(t => Number(t.min));
    if (new Set(mins).size !== mins.length) {
      setMessage({ type: 'error', text: 'Duplicate min thresholds found' });
      return;
    }

    try {
      setSaving(true);
      const res = await axios.put(`${API_URL}/api/admin/poll-rating-tiers`, { tiers });
      setTiers(res.data.tiers);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'Tiers saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save tiers' });
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index, field, value) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: field === 'min' ? (value === '' ? '' : Number(value)) : value };
    setTiers(updated);
    setHasChanges(true);
  };

  const addTier = () => {
    const maxMin = tiers.length > 0 ? Math.max(...tiers.map(t => t.min || 0)) : 0;
    setTiers([...tiers, {
      id: `tier_${Date.now()}`,
      title: '',
      min: maxMin + 100,
      max: null,
      icon: 'star',
      color: '#6B7280',
    }]);
    setHasChanges(true);
  };

  const removeTier = (index) => {
    if (tiers.length <= 1) {
      setMessage({ type: 'error', text: 'At least one tier is required' });
      return;
    }
    setTiers(tiers.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500 py-12">Loading tiers...</div>
      </div>
    );
  }

  const sortedTiers = [...tiers].sort((a, b) => (a.min || 0) - (b.min || 0));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-rubik" data-testid="admin-poll-ratings-title">Poll Rating Tiers</h1>
          <p className="text-sm text-gray-500 font-rubik mt-1">Configure the rating titles and point thresholds users see</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadTiers}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik"
            data-testid="reset-tiers-btn"
          >
            <MdRefresh /> Reset
          </button>
          <button
            onClick={addTier}
            className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik"
            data-testid="add-tier-btn"
          >
            <MdAdd /> Add Tier
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-5 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-rubik font-semibold ${
              hasChanges ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="save-tiers-btn"
          >
            <MdSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-rubik ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="tier-message"
        >
          {message.text}
        </div>
      )}

      {/* Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 font-rubik">Preview</h2>
        <div className="flex flex-wrap gap-3">
          {sortedTiers.map((tier, i) => {
            const IconComp = ICON_MAP[tier.icon] || FaStar;
            return (
              <div
                key={tier.id || i}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                style={{ borderColor: tier.color + '40', backgroundColor: tier.color + '10' }}
              >
                <IconComp style={{ color: tier.color }} />
                <span className="text-sm font-semibold font-rubik" style={{ color: tier.color }}>{tier.title || 'Untitled'}</span>
                <span className="text-xs text-gray-400 font-rubik">{tier.min}+</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tiers List */}
      <div className="space-y-3">
        {sortedTiers.map((tier, index) => {
          const realIndex = tiers.findIndex(t => t === tier);
          const IconComp = ICON_MAP[tier.icon] || FaStar;
          return (
            <div
              key={tier.id || index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
              data-testid={`tier-row-${index}`}
            >
              <div className="flex items-center gap-4">
                <MdDragIndicator className="text-gray-300 text-xl flex-shrink-0" />
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: tier.color + '20' }}
                >
                  <IconComp style={{ color: tier.color }} size={20} />
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3 items-center min-w-0">
                  {/* Title */}
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Title</label>
                    <input
                      type="text"
                      value={tier.title}
                      onChange={(e) => updateTier(realIndex, 'title', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                      placeholder="Tier title"
                      data-testid={`tier-title-${index}`}
                    />
                  </div>
                  {/* Min Threshold */}
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Min Points</label>
                    <input
                      type="number"
                      min="0"
                      value={tier.min}
                      onChange={(e) => updateTier(realIndex, 'min', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                      data-testid={`tier-min-${index}`}
                    />
                  </div>
                  {/* Icon */}
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Icon</label>
                    <select
                      value={tier.icon}
                      onChange={(e) => updateTier(realIndex, 'icon', e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none bg-white"
                      data-testid={`tier-icon-${index}`}
                    >
                      {AVAILABLE_ICONS.map(ic => (
                        <option key={ic.id} value={ic.id}>{ic.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Color */}
                  <div>
                    <label className="text-xs text-gray-400 font-rubik block mb-1">Color</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        value={tier.color}
                        onChange={(e) => updateTier(realIndex, 'color', e.target.value)}
                        className="w-8 h-8 rounded border-0 cursor-pointer flex-shrink-0"
                        data-testid={`tier-color-${index}`}
                      />
                      <input
                        type="text"
                        value={tier.color}
                        onChange={(e) => updateTier(realIndex, 'color', e.target.value)}
                        className="w-full min-w-0 px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-rubik focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                        data-testid={`tier-color-text-${index}`}
                      />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeTier(realIndex)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors flex-shrink-0"
                  title="Remove tier"
                  data-testid={`tier-delete-${index}`}
                >
                  <MdDelete size={18} />
                </button>
              </div>
              {/* Auto-calculated range display */}
              <div className="mt-2 ml-14 text-xs text-gray-400 font-rubik">
                Range: {tier.min} {tier.max != null ? `- ${tier.max}` : '+'} points
              </div>
            </div>
          );
        })}
      </div>

      {tiers.length === 0 && (
        <div className="text-center py-12 text-gray-400 font-rubik">
          No tiers configured. Click "Add Tier" to create one.
        </div>
      )}
    </div>
  );
}

export default AdminPollRatings;
